import React, { useEffect, useState, useCallback } from "react";
import {
  Box, Typography, TextField, Button, Grid,
  FormControl, InputLabel, Select, MenuItem, OutlinedInput, Chip,
} from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import { useIntl } from "react-intl";
import {
  fetchFinancialAssetCategory,
  updateFinancialAssetCategory,
  fetchFinancialAssetTypes,
} from "../../api/companyApi";
import { useNotification } from "../../providers/NotificationProvider";

export default function FinancialAssetCategoryEditPage() {
  const { businessDomainId, categoryId } = useParams();
  const navigate = useNavigate();
  const intl = useIntl();
  const notify = useNotification();
  const t = (id) => intl.formatMessage({ id });

  const [original, setOriginal] = useState(null);
  const [financialAssetTypes, setFinancialAssetTypes] = useState([]);
  const [selectedTypes, setSelectedTypes] = useState([]);
  const [form, setForm] = useState({
    financialAssetCategoryName: "",
    financialAssetCategoryDescription: "",
  });

  const loadData = useCallback(async () => {
    try {
      const [d, types] = await Promise.all([
        fetchFinancialAssetCategory(categoryId),
        fetchFinancialAssetTypes(businessDomainId),
      ]);
      setOriginal(d);
      setForm({
        financialAssetCategoryName: d.financialAssetCategoryName || "",
        financialAssetCategoryDescription: d.financialAssetCategoryDescription || "",
      });
      const allTypes = Array.isArray(types) ? types : [];
      setFinancialAssetTypes(allTypes);
      // 既存の関連タイプを初期値にセット（IDで照合）
      const existing = Array.isArray(d.associatedFinancialAssetTypes) ? d.associatedFinancialAssetTypes : [];
      const existingIds = new Set(existing.map((a) => a.financialAssetTypeId));
      setSelectedTypes(allTypes.filter((ft) => existingIds.has(ft.financialAssetTypeId)));
    } catch (e) { notify.apiError(e); }
  }, [categoryId, businessDomainId]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleTypesChange = (e) => {
    const values = e.target.value;
    setSelectedTypes(typeof values === "string" ? [] : values);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!original) return;
    try {
      await updateFinancialAssetCategory(categoryId, {
        ...original,
        ...form,
        associatedFinancialAssetTypes: selectedTypes.map((ft) => ({
          financialAssetTypeId: ft.financialAssetTypeId,
          financialAssetTypeName: ft.financialAssetTypeName,
        })),
      });
      notify.success(t("message.update.success"));
      navigate(`/strategy/${businessDomainId}/financial-asset-categories/${categoryId}`);
    } catch (err) { notify.apiError(err); }
  };

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>{t("financialAssetCategory.edit.title")}</Typography>
      <Box component="form" onSubmit={handleSubmit}>
        <Grid container spacing={2}>
          <Grid size={12}>
            <TextField fullWidth required
              label={t("financialAssetCategory.name")}
              name="financialAssetCategoryName"
              value={form.financialAssetCategoryName}
              onChange={handleChange} />
          </Grid>
          <Grid size={12}>
            <FormControl fullWidth>
              <InputLabel>{t("financialAssetCategory.financialAssetTypes")}</InputLabel>
              <Select
                multiple
                value={selectedTypes}
                onChange={handleTypesChange}
                input={<OutlinedInput label={t("financialAssetCategory.financialAssetTypes")} />}
                renderValue={(selected) => (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {selected.map((v) => (
                      <Chip key={v.financialAssetTypeId} label={v.financialAssetTypeName} size="small" />
                    ))}
                  </Box>
                )}
              >
                {financialAssetTypes.map((ft) => (
                  <MenuItem key={ft.financialAssetTypeId} value={ft}>
                    {ft.financialAssetTypeName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={12}>
            <TextField fullWidth multiline rows={3}
              label={t("common.description")}
              name="financialAssetCategoryDescription"
              value={form.financialAssetCategoryDescription}
              onChange={handleChange} />
          </Grid>
        </Grid>
        <Box sx={{ mt: 3, display: "flex", gap: 2 }}>
          <Button variant="contained" type="submit">{t("common.save")}</Button>
          <Button variant="outlined" onClick={() => navigate(`/strategy/${businessDomainId}/financial-asset-categories/${categoryId}`)}>{t("common.cancel")}</Button>
        </Box>
      </Box>
    </Box>
  );
}
